import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useClerk, useUser } from "@clerk/react";
import { toast } from "sonner";
import { ArrowRight, Gift, Shield, Receipt, UserRound, LogOut, Clock3, Loader2 } from "lucide-react";
import { Btn, BtnLink, Card, Eyebrow, ProgressBar } from "@/components/kit";
import { FullLoader, useMeQuery } from "@/components/auth/AccessGate";
import { useProducts, ProductStatus } from "@/components/platform/Products";
import { useApi } from "@/lib/api";
import { normalize } from "@/modules/premiere-video/store";
import { doneCount } from "@/modules/premiere-video/logic";

export function RequireSignIn({ children }) {
  const q = useMeQuery();
  if (!q.isLoaded) return <FullLoader label="Chargement…" />;
  if (!q.isSignedIn) return <Navigate to="/sign-in" replace />;
  if (q.error) return <p className="px-5 py-24 text-center text-soft">Impossible de charger ton compte. Actualise la page.</p>;
  if (!q.me) return <FullLoader label="Chargement…" />;
  return children(q.me);
}

function UgcGoCard({ me }) {
  const api = useApi();
  const [done, setDone] = useState(null);
  useEffect(() => {
    if (!me.has_access) return;
    api("GET", "/progress").then((r) => setDone(r.data ? doneCount(normalize(r.data)) : 0)).catch(() => setDone(0));
  }, [me.has_access, api]);
  return (
    <Card className="flex flex-col" data-testid="myspace-ugcgo">
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bx font-display text-white">UG</span>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${me.has_access ? "bg-bx text-white" : "border border-line text-soft"}`} data-testid="myspace-ugcgo-status">
          {me.has_access ? "Acheté" : "Pas encore acheté"}
        </span>
      </div>
      <h3 className="mt-5 font-display text-2xl font-semibold text-ink">UGC GO</h3>
      <p className="mt-1 text-sm text-soft">Ma première vidéo UGC · 7 missions</p>
      {me.has_access ? (
        <>
          <div className="mt-6">
            <div className="flex justify-between text-sm"><span>Progression</span><span className="font-semibold text-bx" data-testid="myspace-ugcgo-progress">{done === null ? "…" : `${done}/7 missions`}</span></div>
            <ProgressBar value={((done || 0) / 7) * 100} className="mt-2" />
          </div>
          <BtnLink to="/parcours" className="mt-6 self-start" data-testid="myspace-ugcgo-open">Reprendre UGC GO<ArrowRight className="h-4 w-4" /></BtnLink>
        </>
      ) : (
        <BtnLink to="/acces" className="mt-6 self-start" data-testid="myspace-ugcgo-buy">Débloquer UGC GO · 9 €</BtnLink>
      )}
    </Card>
  );
}

export function MySpacePage() {
  const { products } = useProducts();
  const { user } = useUser();
  return (
    <RequireSignIn>
      {(me) => (
        <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8 md:py-16" data-testid="myspace-page">
          <Eyebrow>Mon espace</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold text-bx sm:text-5xl">Bonjour{user?.firstName ? ` ${user.firstName}` : ""}.</h1>
          <p className="mt-3 text-soft">Tes produits, ta progression et tes accès rapides.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <UgcGoCard me={me} />
            <Card data-testid="myspace-quick">
              <h3 className="font-display text-xl font-semibold text-ink">Accès rapides</h3>
              <ul className="mt-4 divide-y divide-line">
                {[
                  { to: "/mon-compte", Icon: UserRound, t: "Mon compte", id: "account" },
                  { to: "/offrir", Icon: Gift, t: "Offrir UGC GO", id: "gift" },
                  { to: "/applications", Icon: ArrowRight, t: "Toutes les applications", id: "apps" },
                  ...(me.role === "admin" ? [{ to: "/administration", Icon: Shield, t: "Administration", id: "admin" }] : []),
                ].map(({ to, Icon, t, id }) => (
                  <li key={to}><Link to={to} className="flex items-center gap-3 py-3.5 text-ink hover:text-bx" data-testid={`myspace-quick-${id}`}><Icon className="h-4 w-4 text-gold" />{t}</Link></li>
                ))}
              </ul>
            </Card>
          </div>
          {products?.some((p) => p.status === "soon") && (
            <>
              <h2 className="mt-14 font-display text-2xl font-semibold text-ink">Bientôt sur UGC & CO</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {products.filter((p) => p.status === "soon").map((p) => (
                  <Link key={p.slug} to={`/applications/${p.slug}`} className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-white px-5 py-4 hover:border-bx/30" data-testid={`myspace-soon-${p.slug}`}>
                    <span className="font-medium text-ink">{p.name}</span><ProductStatus p={p} />
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </RequireSignIn>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");
const fmtAmount = (o) => (o.amount != null ? `${o.amount.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${String(o.currency || "eur").toUpperCase() === "EUR" ? "€" : o.currency}` : "");

function OrderRow({ o }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const receipt = async () => {
    setBusy(true);
    try {
      const { receipt_url } = await api("GET", `/me/orders/${o.session_id}/receipt`);
      window.open(receipt_url, "_blank", "noopener");
    } catch {
      toast.error("Reçu indisponible pour cet achat. Il t'a aussi été envoyé par email par Stripe.");
    }
    setBusy(false);
  };
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`order-${o.session_id}`}>
      <div>
        <p className="font-medium text-ink">{o.product}{o.kind === "gift" ? " · en cadeau" : ""}</p>
        <p className="text-sm text-soft">{fmtDate(o.created_at)} · {fmtAmount(o)}{o.payment_status === "refunded" ? " · remboursé" : ""}</p>
      </div>
      <Btn variant="outline" size="sm" onClick={receipt} disabled={busy} data-testid="order-receipt-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />}Reçu
      </Btn>
    </li>
  );
}

export function MyAccountPage() {
  const { user } = useUser();
  const clerk = useClerk();
  const api = useApi();
  const [orders, setOrders] = useState(null);
  useEffect(() => { api("GET", "/me/orders").then((r) => setOrders(r.orders)).catch(() => setOrders([])); }, [api]);
  return (
    <RequireSignIn>
      {() => (
        <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 md:py-16" data-testid="account-page">
          <Eyebrow>Mon compte</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold text-bx">Mon compte</h1>
          <Card className="mt-8" data-testid="account-profile">
            <h2 className="font-display text-xl font-semibold text-ink">Profil</h2>
            <p className="mt-3 text-soft">Email : <span className="text-ink" data-testid="account-email">{user?.primaryEmailAddress?.emailAddress}</span></p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Btn variant="outline" onClick={() => clerk.openUserProfile()} data-testid="account-edit-profile"><UserRound className="h-4 w-4" />Modifier mon profil et mon mot de passe</Btn>
              <Btn variant="ghost" onClick={() => clerk.signOut({ redirectUrl: "/" })} data-testid="account-sign-out"><LogOut className="h-4 w-4" />Me déconnecter</Btn>
            </div>
          </Card>
          <Card className="mt-6" data-testid="account-orders">
            <h2 className="font-display text-xl font-semibold text-ink">Mes achats et reçus</h2>
            {orders === null ? <p className="mt-4 text-soft">Chargement…</p> : orders.length === 0 ? (
              <p className="mt-4 text-soft" data-testid="account-no-orders">Aucun achat pour le moment.</p>
            ) : <ul className="mt-2 divide-y divide-line">{orders.map((o) => <OrderRow key={o.session_id} o={o} />)}</ul>}
            <p className="mt-4 flex items-center gap-2 text-xs text-soft"><Clock3 className="h-3.5 w-3.5" />Le reçu Stripe fait office de justificatif d'achat. Les factures détaillées arriveront prochainement.</p>
          </Card>
        </div>
      )}
    </RequireSignIn>
  );
}
